import type {
  AdminLeadAppointmentConversionOutcome,
  AdminLeadAppointmentConversionRecord,
  AdminLeadAppointmentConversionRequest,
  AdminLeadDetail,
  IdempotencyKey,
} from "@whitehaven/contracts";
import { ContentState, FormField, Select, StatusPill } from "@whitehaven/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
} from "../../protected-state";
import type {
  AdminLeadAppointmentConversionGateways,
  AdminLeadAppointmentConversionPreflight,
  AdminLeadAppointmentHoldCandidate,
} from "./lead-appointment-conversion-gateway";

type PanelState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "unavailable" }
  | {
      readonly kind: "ready";
      readonly preflight: AdminLeadAppointmentConversionPreflight;
    }
  | {
      readonly kind: "confirmed";
      readonly conversion: AdminLeadAppointmentConversionRecord;
    };

type MutationNotice =
  | "CONFLICT"
  | "HOLD_EXPIRED"
  | "LEAD_NOT_CONVERTED"
  | "REJECTED"
  | "FAILED"
  | null;

export interface AdminLeadAppointmentConversionPanelProps {
  readonly detail: AdminLeadDetail;
  readonly gateways: AdminLeadAppointmentConversionGateways | null;
  readonly detailRefreshing: boolean;
  readonly externalMutationPending: boolean;
  readonly onPendingChange: (pending: boolean) => void;
  readonly onRefreshRequired: () => void;
}

const appointmentDateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Pacific/Honolulu",
});

function formatAppointmentTime(value: string) {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? appointmentDateFormatter.format(date)
    : "Unavailable";
}

function createIdempotencyKey(): IdempotencyKey | null {
  try {
    const value = globalThis.crypto?.randomUUID?.();
    return value && /^[A-Za-z0-9_-]{8,128}$/.test(value)
      ? (value as IdempotencyKey)
      : null;
  } catch {
    return null;
  }
}

function mutationNoticeFor(
  outcome: AdminLeadAppointmentConversionOutcome,
): MutationNotice {
  switch (outcome.kind) {
    case "conflict":
      return "CONFLICT";
    case "hold-expired":
      return "HOLD_EXPIRED";
    case "lead-not-converted":
      return "LEAD_NOT_CONVERTED";
    case "rejected":
      return "REJECTED";
    default:
      return null;
  }
}

function HoldSummary({
  candidate,
}: {
  readonly candidate: AdminLeadAppointmentHoldCandidate;
}) {
  return (
    <dl>
      <div>
        <dt>Service</dt>
        <dd>{candidate.serviceLabel}</dd>
      </div>
      <div>
        <dt>Location</dt>
        <dd>{candidate.locationLabel}</dd>
      </div>
      <div>
        <dt>Starts</dt>
        <dd>
          <time dateTime={candidate.startsAt}>
            {formatAppointmentTime(candidate.startsAt)}
          </time>
        </dd>
      </div>
      <div>
        <dt>Hold expires</dt>
        <dd>
          <time dateTime={candidate.expiresAt}>
            {formatAppointmentTime(candidate.expiresAt)}
          </time>
        </dd>
      </div>
    </dl>
  );
}

export function AdminLeadAppointmentConversionPanel({
  detail,
  gateways,
  detailRefreshing,
  externalMutationPending,
  onPendingChange,
  onRefreshRequired,
}: AdminLeadAppointmentConversionPanelProps) {
  const guestConversion = detail.guestConversion;
  const [state, setState] = useState<PanelState>(
    guestConversion && gateways ? { kind: "loading" } : { kind: "unavailable" },
  );
  const [selectedHoldId, setSelectedHoldId] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<MutationNotice>(null);
  const requestSequence = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);

  useEffect(() => {
    requestSequence.current += 1;
    activeRequest.current?.abort();
    activeRequest.current = null;
    setSelectedHoldId("");
    setReviewing(false);
    setNotice(null);

    if (!guestConversion || !gateways) {
      setState({ kind: "unavailable" });
      return;
    }

    const requestId = requestSequence.current;
    const controller = new AbortController();
    activeRequest.current = controller;
    const protectedStateGeneration = captureProtectedStateGeneration();
    setState({ kind: "loading" });

    void gateways
      .read(
        {
          leadId: detail.summary.leadId,
          guestId: guestConversion.guestId,
          expectedLeadUpdatedAt: detail.summary.updatedAt,
        },
        { signal: controller.signal },
      )
      .then((outcome) => {
        if (
          controller.signal.aborted ||
          requestId !== requestSequence.current ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }
        if (outcome.kind === "ready") {
          setState({ kind: "ready", preflight: outcome.preflight });
          setSelectedHoldId(outcome.preflight.holds[0]?.holdId ?? "");
        } else if (outcome.kind === "already-converted") {
          setState({ kind: "confirmed", conversion: outcome.conversion });
        } else {
          setState({ kind: "unavailable" });
        }
      })
      .catch(() => {
        if (
          !controller.signal.aborted &&
          requestId === requestSequence.current &&
          isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          setState({ kind: "error" });
        }
      });

    return () => controller.abort();
  }, [
    detail.summary.leadId,
    detail.summary.updatedAt,
    gateways,
    guestConversion,
  ]);

  const selectedHold = useMemo(
    () =>
      state.kind === "ready"
        ? (state.preflight.holds.find(
            (candidate) => candidate.holdId === selectedHoldId,
          ) ?? null)
        : null,
    [selectedHoldId, state],
  );

  const blocked =
    pending || detailRefreshing || externalMutationPending || !selectedHold;

  const confirmConversion = async () => {
    if (state.kind !== "ready" || !selectedHold || !gateways || blocked) {
      return;
    }
    const idempotencyKey = createIdempotencyKey();
    if (!idempotencyKey) {
      setNotice("FAILED");
      return;
    }

    const request: AdminLeadAppointmentConversionRequest = {
      leadId: state.preflight.leadId,
      expectedLeadUpdatedAt: state.preflight.expectedLeadUpdatedAt,
      sourceHoldId: selectedHold.holdId,
      expectedHoldRevision: selectedHold.revision,
      expectedHoldExpiresAt: selectedHold.expiresAt,
      conversionPreflightRevision: state.preflight.conversionPreflightRevision,
    };
    const controller = new AbortController();
    activeRequest.current?.abort();
    activeRequest.current = controller;
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;
    const protectedStateGeneration = captureProtectedStateGeneration();

    setPending(true);
    onPendingChange(true);
    setNotice(null);
    try {
      const outcome = await gateways.mutate(request, {
        idempotencyKey,
        signal: controller.signal,
      });
      if (
        controller.signal.aborted ||
        requestId !== requestSequence.current ||
        !isProtectedStateGenerationCurrent(protectedStateGeneration)
      ) {
        return;
      }
      if (outcome.kind === "confirmed" || outcome.kind === "replayed") {
        setState({ kind: "confirmed", conversion: outcome.conversion });
        setReviewing(false);
      } else {
        setNotice(mutationNoticeFor(outcome));
        if (
          outcome.kind === "conflict" ||
          outcome.kind === "hold-expired" ||
          outcome.kind === "lead-not-converted"
        ) {
          onRefreshRequired();
        }
      }
    } catch {
      if (
        !controller.signal.aborted &&
        requestId === requestSequence.current &&
        isProtectedStateGenerationCurrent(protectedStateGeneration)
      ) {
        setNotice("FAILED");
      }
    } finally {
      if (requestId === requestSequence.current) {
        setPending(false);
        onPendingChange(false);
      }
    }
  };

  return (
    <section
      className="crm-lead-detail__card crm-lead-appointment-conversion"
      aria-labelledby="crm-lead-appointment-conversion-title"
      aria-busy={pending || state.kind === "loading" ? "true" : undefined}
    >
      <h3 id="crm-lead-appointment-conversion-title">Initial appointment</h3>
      <p>This does not move the CRM pipeline stage.</p>

      {!guestConversion && (
        <ContentState
          kind="warning"
          headingLevel={4}
          title="Create or link the guest record first."
        >
          <p>
            Appointment conversion stays unavailable until the lead-to-guest
            conversion above is confirmed.
          </p>
        </ContentState>
      )}

      {guestConversion && !gateways && (
        <ContentState
          kind="warning"
          headingLevel={4}
          title="The scheduling conversion service is not connected."
        >
          <p>No appointment or hold is changed.</p>
        </ContentState>
      )}

      {guestConversion && gateways && state.kind === "loading" && (
        <ContentState
          kind="loading"
          headingLevel={4}
          title="Loading active appointment holds."
        />
      )}

      {state.kind === "error" && (
        <ContentState
          kind="error"
          headingLevel={4}
          title="Active appointment holds could not be loaded."
          actions={
            <button type="button" onClick={onRefreshRequired}>
              Refresh lead
            </button>
          }
        />
      )}

      {state.kind === "ready" && state.preflight.holds.length === 0 && (
        <ContentState
          kind="empty"
          headingLevel={4}
          title="No active appointment hold is available."
        >
          <p>Create a short-lived hold in scheduling before converting.</p>
        </ContentState>
      )}

      {state.kind === "ready" && state.preflight.holds.length > 0 && (
        <>
          <FormField
            id="crm-lead-appointment-hold"
            label="Active appointment hold"
            hint="Only current, guest-bound holds returned by the scheduling service are listed."
          >
            {(controlProps) => (
              <Select
                {...controlProps}
                value={selectedHoldId}
                disabled={pending || detailRefreshing}
                onChange={(event) => {
                  setSelectedHoldId(event.currentTarget.value);
                  setReviewing(false);
                  setNotice(null);
                }}
              >
                {state.preflight.holds.map((candidate) => (
                  <option key={candidate.holdId} value={candidate.holdId}>
                    {candidate.serviceLabel} ·{" "}
                    {formatAppointmentTime(candidate.startsAt)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          {selectedHold && <HoldSummary candidate={selectedHold} />}
          {!reviewing ? (
            <button
              type="button"
              className="crm-lead-detail__button"
              disabled={blocked}
              onClick={() => setReviewing(true)}
            >
              Review appointment conversion
            </button>
          ) : (
            <ContentState
              kind="warning"
              headingLevel={4}
              title="Confirm this lead-to-appointment conversion."
              actions={
                <>
                  <button
                    type="button"
                    disabled={blocked}
                    onClick={() => void confirmConversion()}
                  >
                    {pending ? "Confirming" : "Confirm appointment"}
                  </button>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => setReviewing(false)}
                  >
                    Cancel
                  </button>
                </>
              }
            >
              <p>
                The scheduling and CRM records are committed together. A
                conflict or expired hold leaves both unchanged.
              </p>
            </ContentState>
          )}
        </>
      )}

      {state.kind === "confirmed" && (
        <ContentState
          kind="success"
          headingLevel={4}
          title="Initial appointment confirmed."
        >
          <p>
            <StatusPill tone="positive">Confirmed</StatusPill> Appointment{" "}
            <code>{state.conversion.appointmentId}</code> is linked to the guest
            and CRM lead.
          </p>
        </ContentState>
      )}

      {notice && (
        <ContentState
          kind="error"
          headingLevel={4}
          title={
            notice === "HOLD_EXPIRED"
              ? "The appointment hold expired."
              : notice === "LEAD_NOT_CONVERTED"
                ? "The lead no longer has a current guest conversion."
                : notice === "CONFLICT"
                  ? "The lead or hold changed before confirmation."
                  : "The appointment could not be confirmed."
          }
        >
          <p>
            No partial appointment is shown. Refresh and choose a current hold
            before trying again.
          </p>
        </ContentState>
      )}
    </section>
  );
}
